import { useState, useEffect, useMemo } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Filter, Search } from 'lucide-react';

// Dashed filter button that opens a searchable multi-select list (same look as the Custom Mappings filters).
// options: [{ value, label }], selected: [value]
export default function MultiSelectFilter({ label, options, selected, onSelectionChange, testIdPrefix = '' }) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredOptions = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return options;
    return options.filter(o => o.label.toLowerCase().includes(term));
  }, [options, searchTerm]);

  useEffect(() => {
    if (!open) setSearchTerm('');
  }, [open]);

  const toggleOption = (value) => {
    onSelectionChange(selected.includes(value) ? selected.filter(v => v !== value) : [...selected, value]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="h-8 border-dashed" data-testid={`${testIdPrefix}filter-trigger`}>
          <Filter className="mr-2 h-3 w-3" />
          {label}
          {selected.length > 0 && (
            <Badge variant="secondary" className="ml-2 rounded-sm px-1 font-normal">{selected.length}</Badge>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[250px] p-0" align="start">
        <div className="p-2 border-b">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">{label}</span>
            <div className="flex gap-1">
              {filteredOptions.length > 0 && (
                <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => onSelectionChange(filteredOptions.map(o => o.value))}>
                  All
                </Button>
              )}
              {selected.length > 0 && (
                <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => onSelectionChange([])}>
                  Clear
                </Button>
              )}
            </div>
          </div>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
            <Input placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="h-8 pl-7 text-sm" />
          </div>
        </div>
        <div className="max-h-[300px] overflow-auto p-2">
          {filteredOptions.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2 text-center">
              {options.length === 0 ? 'No options' : 'No matches found'}
            </p>
          ) : (
            filteredOptions.map((option) => (
              <div
                key={option.value}
                className="flex items-center space-x-2 py-1 px-1 rounded hover:bg-gray-100 cursor-pointer"
                onClick={() => toggleOption(option.value)}
                data-testid={`${testIdPrefix}option-${option.value}`}
              >
                <Checkbox checked={selected.includes(option.value)} className="pointer-events-none" />
                <span className="text-sm truncate flex-1" title={option.label}>{option.label}</span>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
